/**
 * Builds client-side chart specs from loaded platform data when the chat API
 * does not return an image artifact.
 */

const CHART_INTENT_RE = /\b(line|bar|area)?\s*(chart|graph|plot)\b/i;
const FIELD_PATTERNS = [
  { pattern: /water[_\s-]?temperature(?:[_\s-]?c)?/i, keys: ['water_temperature_c', 'temp', 'temperature'] },
  { pattern: /message[_\s-]?speed[_\s-]?over[_\s-]?ground|speed over ground/i, keys: ['message_speed_over_ground', 'speed', 'nspeed'] },
  { pattern: /course over ground|heading|direction/i, keys: ['message_course_over_ground', 'direction', 'heading'] },
  { pattern: /salinity/i, keys: ['water_salinity_psu', 'salinity'] },
  { pattern: /pressure/i, keys: ['pressure_dbars', 'pressure'] },
  { pattern: /wave height|ssh/i, keys: ['ssh', 'waveHeight'] },
  { pattern: /wind speed/i, keys: ['nspeed', 'windspeed', 'speed'] },
  { pattern: /sound speed/i, keys: ['sound_speed_ms', 'soundSpeed'] },
  { pattern: /temperature/i, keys: ['temp', 'temperature', 'water_temperature_c'] }
];

const CHART_TYPE_LABELS = {
  line: 'Line chart',
  area: 'Area chart',
  bar: 'Bar chart'
};

const pickField = (rows, candidates) => {
  if (!rows?.length || !candidates?.length) return null;

  const sample = rows.find(Boolean) || {};
  for (const key of candidates) {
    if (sample[key] !== undefined && sample[key] !== null && sample[key] !== '') {
      return key;
    }
  }
  return null;
};

const formatTimeLabel = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

export const detectChartIntent = (text) => {
  if (!text || typeof text !== 'string') return null;

  const match = text.match(CHART_INTENT_RE);
  if (!match) return null;

  const lower = text.toLowerCase();
  let chartType = 'line';
  if (/\bbar chart\b|\bbar graph\b/.test(lower)) chartType = 'bar';
  else if (/\barea chart\b|\barea graph\b/.test(lower)) chartType = 'area';
  else if (match[1]) chartType = match[1].toLowerCase();

  let fieldKeys = null;
  for (const entry of FIELD_PATTERNS) {
    if (entry.pattern.test(text)) {
      fieldKeys = entry.keys;
      break;
    }
  }

  return { chartType, fieldKeys };
};

export const buildChartFromData = (rows, intent, { maxPoints = 240 } = {}) => {
  if (!rows?.length || !intent) return null;

  const field =
    pickField(rows, intent.fieldKeys) ||
    pickField(rows, ['water_temperature_c', 'temp', 'message_speed_over_ground', 'speed', 'salinity']);

  if (!field) return null;

  const sorted = [...rows]
    .filter((row) => row?.time && row[field] != null && row[field] !== '' && !Number.isNaN(Number(row[field])))
    .sort((a, b) => new Date(a.time) - new Date(b.time));

  if (sorted.length < 2) return null;

  const sampled = sorted.length > maxPoints
    ? sorted.filter((_, index) => index % Math.ceil(sorted.length / maxPoints) === 0)
    : sorted;

  const data = sampled.map((row) => ({
    time: formatTimeLabel(row.time),
    value: Number(row[field])
  }));

  const label = field
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

  return {
    type: intent.chartType || 'line',
    title: `${CHART_TYPE_LABELS[intent.chartType] || 'Chart'} — ${label}`,
    xKey: 'time',
    yKey: 'value',
    yLabel: label,
    data
  };
};

export const resolveChartForConversation = ({ userMessage, assistantMessage, rows, apiImages = [] }) => {
  if (apiImages.length > 0) return null;

  const combined = [userMessage, assistantMessage].filter(Boolean).join('\n');
  const intent = detectChartIntent(combined);
  if (!intent) return null;

  return buildChartFromData(rows, intent);
};
